import React, { ReactNode, forwardRef, useCallback, ComponentType, useMemo } from 'react';

import { SCP } from 'ia-react-core/~/interfaces/SCP';

import Loader from '../../loader/Loader';
import { StyledButton, StyledContentWrapper, StyledIcon, StyledIconWrapper } from './IconButton.styles';

export interface IconButtonProps extends SCP<'button'> {
  /** Additional content to display in the button. */
  children?: ReactNode;
  /** Text describing the action of the button. */
  label: string;
  /** Deativates the button and displays a loader. */
  busy?: boolean;
  /** Icon component to display in the button. */
  Icon?: ComponentType;
  /** Style the icon in white. */
  white?: boolean;
  /** Style the background of the primary color. */
  primaryBackground?: boolean;
  /** Number of pixels of the button size. */
  buttonSize?: number;
  /** Number of pixels for the icon size. */
  iconSize?: number;
  /** Disables the button. */
  disabled?: boolean;
  /** Use an aria-label instead of a html title attribute. */
  noTitle?: boolean;
}

const IconButtonDefaultProps = {
  buttonSize: 44,
  iconSize: 24,
};

const IconButton = Object.assign(
  forwardRef<HTMLButtonElement, IconButtonProps>(({
    children, label, Icon, busy, disabled, iconSize, primaryBackground, noTitle, ...props
  }, ref) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const As = useCallback(({ iconSize: i, ...p }: { iconSize?: number }) => <Icon {...p} />, [Icon]);

    const accessiblityProps = useMemo(() => noTitle ? {
      'aria-label': label,
    } : { title: label }, [label, noTitle]);

    return (
      <StyledButton
        disabled={disabled}
        data-testid="IconButton"
        type="button"
        primaryBackground={primaryBackground}
        {...accessiblityProps}
        {...props}
        ref={ref}
      >
        <StyledContentWrapper>
          {children}
          {!busy && <StyledIconWrapper><StyledIcon as={As} iconSize={iconSize} /></StyledIconWrapper>}
          {busy && <Loader size={24} />}
        </StyledContentWrapper>
      </StyledButton>
    );
  }),
  {
    defaultProps: IconButtonDefaultProps,
    StyledButton,
  },
);

export default IconButton;
